/* ==========================================================================
   LUMERIVA — theme.js
   Vanilla, dependency-free. Modules:
   Header · Drawers · Cart (Ajax + section rendering) · Predictive search
   Product form (variants, media, price) · Gallery · Quantity · Reveal
   Sticky ATC · Share · Announcement rotation · Facets · Localization
   ========================================================================== */
(function () {
  'use strict';

  const cfg = window.lumeriva || { routes: {}, strings: {} };
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function formatMoney(cents) {
    const format = cfg.moneyFormat || '€{{amount_with_comma_separator}}';
    const value = (cents / 100).toFixed(2);
    const [int, dec] = value.split('.');
    const withDots = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const withCommas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${withDots},${dec}`)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, withDots)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${dec}`);
  }

  function debounce(fn, wait) {
    let t;
    return function (...args) { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), wait); };
  }

  function trapFocus(container) {
    const focusable = $$('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])', container)
      .filter(el => el.offsetParent !== null);
    if (!focusable.length) return () => {};
    const first = focusable[0], last = focusable[focusable.length - 1];
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  }

  /* -----------------------------------------------------------------------
     Header: scrolled state + hide on scroll down (subtle)
     ----------------------------------------------------------------------- */
  const Header = {
    init() {
      this.el = $('[data-header]');
      if (!this.el) return;
      this.lastY = window.scrollY;
      this.hideOnScroll = this.el.dataset.hideOnScroll === 'true';
      this.update();
      window.addEventListener('scroll', () => this.update(), { passive: true });
      this.setHeight();
      window.addEventListener('resize', debounce(() => this.setHeight(), 150));
    },
    setHeight() {
      document.documentElement.style.setProperty('--header-height', this.el.offsetHeight + 'px');
      const wrapper = this.el.closest('.shopify-section');
      if (wrapper && document.body.dataset.stickyHeader === 'true') wrapper.classList.add('section-site-header--sticky');
    },
    update() {
      const y = window.scrollY;
      this.el.classList.toggle('is-scrolled', y > 24);
      if (this.hideOnScroll) {
        const down = y > this.lastY && y > 240;
        this.el.classList.toggle('is-hidden', down && !document.body.classList.contains('drawer-open'));
      }
      this.lastY = y;
    }
  };

  /* -----------------------------------------------------------------------
     Drawers: [data-drawer="id"] panels, [data-drawer-open="id"] triggers
     ----------------------------------------------------------------------- */
  const Drawers = {
    current: null,
    init() {
      this.backdrop = $('[data-drawer-backdrop]');
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-drawer-open]');
        if (opener) { e.preventDefault(); this.open(opener.dataset.drawerOpen, opener); return; }
        const closer = e.target.closest('[data-drawer-close]');
        if (closer) { e.preventDefault(); this.close(); }
      });
      if (this.backdrop) this.backdrop.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.current) this.close(); });
    },
    open(id, opener) {
      const drawer = $(`[data-drawer="${id}"]`);
      if (!drawer) return;
      if (this.current && this.current !== drawer) this.close(true);
      this.current = drawer;
      this.opener = opener || document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      if (this.backdrop) this.backdrop.classList.add('is-open');
      document.body.classList.add('drawer-open');
      this.releaseTrap = trapFocus(drawer);
      const focusTarget = $('[data-drawer-focus]', drawer) || $('button, a, input', drawer);
      setTimeout(() => focusTarget && focusTarget.focus({ preventScroll: true }), 80);
      drawer.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    },
    close(keepBody) {
      if (!this.current) return;
      const drawer = this.current;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (this.releaseTrap) this.releaseTrap();
      if (!keepBody) {
        if (this.backdrop) this.backdrop.classList.remove('is-open');
        document.body.classList.remove('drawer-open');
        if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
      }
      this.current = null;
      drawer.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }
  };

  /* -----------------------------------------------------------------------
     Cart: Ajax add / change + re-render drawer via Section Rendering API
     ----------------------------------------------------------------------- */
  const Cart = {
    init() {
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('form[data-product-form]');
        if (form) { e.preventDefault(); this.add(form); }
      });
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-cart-change]');
        if (!btn) return;
        e.preventDefault();
        this.change(btn.dataset.line, parseInt(btn.dataset.cartChange, 10), btn);
      });
      document.addEventListener('change', (e) => {
        const input = e.target.closest('[data-cart-qty]');
        if (!input) return;
        this.change(input.dataset.line, parseInt(input.value, 10) || 0, input);
      });
      document.addEventListener('change', (e) => {
        const note = e.target.closest('[data-cart-note]');
        if (!note) return;
        fetch(cfg.routes.cartUpdate + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) });
      });
    },
    sectionsToRender() {
      const ids = ['cart-drawer'];
      if ($('#MainCart')) ids.push($('#MainCart').dataset.sectionId);
      return ids;
    },
    async add(form) {
      const btn = $('[type="submit"]', form);
      const error = $('[data-form-error]', form);
      if (error) { error.hidden = true; error.textContent = ''; }
      btn.classList.add('btn--loading'); btn.setAttribute('aria-disabled', 'true');
      const body = new FormData(form);
      body.append('sections', this.sectionsToRender().join(','));
      body.append('sections_url', window.location.pathname);
      try {
        const res = await fetch(cfg.routes.cartAdd + '.js', { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || cfg.strings.error);
        this.render(data.sections);
        if (cfg.cartType === 'drawer' && $('[data-drawer="cart"]')) Drawers.open('cart', btn);
        else window.location.href = cfg.routes.cart;
        document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      } catch (err) {
        if (error) { error.textContent = err.message; error.hidden = false; }
      } finally {
        btn.classList.remove('btn--loading'); btn.removeAttribute('aria-disabled');
      }
    },
    async change(line, quantity, trigger) {
      const drawer = $('[data-drawer="cart"]');
      const page = $('#MainCart');
      [drawer, page].forEach(el => el && el.classList.add('is-loading'));
      try {
        const res = await fetch(cfg.routes.cartChange + '.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: this.sectionsToRender(), sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (data.errors) throw new Error(data.errors);
        this.render(data.sections);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      } catch (err) {
        const msg = $('[data-cart-error]');
        if (msg) { msg.textContent = err.message; msg.hidden = false; }
      } finally {
        [drawer, page].forEach(el => el && el.classList.remove('is-loading'));
      }
    },
    render(sections) {
      if (!sections) return;
      Object.keys(sections).forEach(id => {
        const html = new DOMParser().parseFromString(sections[id], 'text/html');
        if (id === 'cart-drawer') {
          const target = $('[data-drawer="cart"] [data-cart-drawer-inner]');
          const source = $('[data-cart-drawer-inner]', html);
          if (target && source) target.innerHTML = source.innerHTML;
        } else {
          const target = $('#MainCart');
          const source = $('#MainCart', html);
          if (target && source) target.innerHTML = source.innerHTML;
        }
        const countSource = $('[data-cart-count-value]', html);
        if (countSource) this.updateCount(parseInt(countSource.textContent, 10) || 0);
      });
    },
    updateCount(count) {
      $$('[data-cart-count]').forEach(el => { el.textContent = count; el.hidden = count === 0; });
      $$('[data-cart-icon]').forEach(el => {
        el.setAttribute('aria-label', count === 1 ? cfg.strings.cartCountOne : (cfg.strings.cartCount || '').replace('[count]', count));
      });
    }
  };

  /* -----------------------------------------------------------------------
     Predictive search
     ----------------------------------------------------------------------- */
  const Search = {
    init() {
      this.form = $('[data-predictive-search]');
      if (!this.form) return;
      this.input = $('input[type="search"]', this.form);
      this.results = $('[data-search-results]');
      this.quick = $('[data-search-quick]');
      this.input.addEventListener('input', debounce(() => this.search(), 260));
      $$('[data-search-clear]').forEach(b => b.addEventListener('click', () => { this.input.value = ''; this.reset(); this.input.focus(); }));
    },
    reset() {
      if (this.results) { this.results.hidden = true; this.results.innerHTML = ''; }
      if (this.quick) this.quick.hidden = false;
    },
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.reset(); return; }
      const url = `${cfg.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page,article&resources[limit]=8&resources[limit_scope]=each&section_id=predictive-search`;
      try {
        const res = await fetch(url);
        const text = await res.text();
        const html = new DOMParser().parseFromString(text, 'text/html');
        const inner = $('#shopify-section-predictive-search', html);
        if (inner && this.results) {
          this.results.innerHTML = inner.innerHTML;
          this.results.hidden = false;
          if (this.quick) this.quick.hidden = true;
        }
      } catch (e) { /* silent */ }
    }
  };

  /* -----------------------------------------------------------------------
     Product form: variants, price, media, availability, URL
     ----------------------------------------------------------------------- */
  class ProductForm {
    constructor(section) {
      this.section = section;
      this.form = $('form[data-product-form]', section);
      const json = $('[data-variants-json]', section);
      if (!this.form || !json) return;
      this.variants = JSON.parse(json.textContent);
      this.idInput = $('[name="id"]', this.form);
      this.optionInputs = $$('[data-option-index]', section);
      this.priceEl = $('[data-product-price]', section);
      this.submit = $('[type="submit"]', this.form);
      this.submitText = $('[data-submit-text]', this.submit) || this.submit;
      this.gallery = section.gallery;
      this.section.addEventListener('change', (e) => {
        if (e.target.closest('[data-option-index]')) this.onChange();
      });
      this.onChange(true);
    }
    selectedOptions() {
      const opts = [];
      this.optionInputs.forEach(input => {
        const idx = parseInt(input.dataset.optionIndex, 10);
        if (input.type === 'radio') { if (input.checked) opts[idx] = input.value; }
        else opts[idx] = input.value;
      });
      return opts;
    }
    onChange(initial) {
      const opts = this.selectedOptions();
      let variant;
      if (this.optionInputs.length === 0) {
        /* Single-variant product (no option selectors): use the variant in the hidden id input, or the first one */
        const currentId = parseInt(this.idInput.value, 10);
        variant = this.variants.find(v => v.id === currentId) || this.variants[0];
      } else {
        variant = this.variants.find(v => v.options.every((o, i) => o === opts[i]));
      }
      this.updateAvailabilityStates(opts);
      if (!variant) { this.setUnavailable(); return; }
      this.idInput.value = variant.id;
      this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      this.updatePrice(variant);
      this.updateButton(variant);
      this.updateLabels(opts);
      $$('[data-variant-sku]', this.section).forEach(el => { el.textContent = variant.sku || ''; el.closest('[data-sku-wrap]') && (el.closest('[data-sku-wrap]').hidden = !variant.sku); });
      if (variant.featured_media && this.gallery) {
        if (!initial) this.gallery.goToMedia(variant.featured_media.id);
        else if (new URL(window.location.href).searchParams.has('variant')) this.gallery.goToMedia(variant.featured_media.id, true);
      }
      if (!initial && history.replaceState) {
        const url = new URL(window.location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url);
      }
      const sticky = $('[data-sticky-price]');
      if (sticky) sticky.innerHTML = this.priceEl ? this.priceEl.innerHTML : '';
    }
    updateLabels(opts) {
      $$('[data-option-value]', this.section).forEach(el => { el.textContent = opts[parseInt(el.dataset.optionValue, 10)] || ''; });
    }
    updateAvailabilityStates(opts) {
      /* Mark values of option N unavailable when no variant with current selections for options < N has stock */
      this.optionInputs.forEach(input => {
        const idx = parseInt(input.dataset.optionIndex, 10);
        if (input.type !== 'radio') return;
        const test = opts.slice(); test[idx] = input.value;
        const match = this.variants.find(v => v.options.every((o, i) => i > idx ? true : o === test[i]) && v.available);
        input.closest('.variant-pill').classList.toggle('variant-pill--unavailable', !match);
      });
    }
    updatePrice(variant) {
      if (!this.priceEl) return;
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      const pct = onSale ? Math.floor((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price) : 0;
      this.priceEl.innerHTML = onSale
        ? `<span class="price__sale">${formatMoney(variant.price)}</span><s class="price__compare">${formatMoney(variant.compare_at_price)}</s>${pct > 0 ? `<span class="price__badge">-${pct}%</span>` : ''}`
        : `<span>${formatMoney(variant.price)}</span>`;
      const omni = $('[data-omnibus]', this.section);
      if (omni) { omni.hidden = !onSale; if (onSale && cfg.strings.omnibus) omni.textContent = cfg.strings.omnibus.replace('{{ price }}', formatMoney(variant.compare_at_price)).replace('{{ pct }}', pct); }
    }
    updateButton(variant) {
      if (!this.submit) return;
      const s = cfg.strings;
      if (variant.available) { this.submit.disabled = false; this.submitText.textContent = s.addToCart; }
      else { this.submit.disabled = true; this.submitText.textContent = s.soldOut; }
      const sticky = $('[data-sticky-submit]');
      if (sticky) { sticky.disabled = this.submit.disabled; sticky.textContent = this.submitText.textContent; }
    }
    setUnavailable() {
      if (!this.submit) return;
      this.submit.disabled = true; this.submitText.textContent = cfg.strings.unavailable;
    }
  }

  /* -----------------------------------------------------------------------
     Gallery: scroll-snap track + dots + thumbs + arrows
     ----------------------------------------------------------------------- */
  class Gallery {
    constructor(el) {
      this.el = el;
      this.track = $('[data-gallery-track]', el);
      this.slides = $$('[data-media-id]', this.track);
      this.dots = $$('[data-gallery-dot]', el);
      this.thumbs = $$('[data-gallery-thumb]', el);
      if (!this.track || this.slides.length < 2) return;
      this.stacked = el.classList.contains('gallery--stacked') && window.matchMedia('(min-width: 900px)').matches;
      this.track.addEventListener('scroll', debounce(() => this.sync(), 80), { passive: true });
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.goTo(i)));
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.goTo(i)));
      $$('[data-gallery-prev]', el).forEach(b => b.addEventListener('click', () => this.goTo(this.index - 1)));
      $$('[data-gallery-next]', el).forEach(b => b.addEventListener('click', () => this.goTo(this.index + 1)));
      this.index = 0;
      /* Pause videos that scroll out of view */
      this.observer = new IntersectionObserver(entries => entries.forEach(en => {
        const v = $('video', en.target); if (v && !en.isIntersecting) v.pause();
      }), { root: this.track, threshold: 0.5 });
      this.slides.forEach(s => this.observer.observe(s));
    }
    step() {
      return this.slides.length > 1 ? (this.slides[1].offsetLeft - this.slides[0].offsetLeft) : this.track.clientWidth;
    }
    sync() {
      this.index = Math.round(this.track.scrollLeft / this.step());
      this.setActive(this.index);
    }
    setActive(i) {
      this.dots.forEach((d, j) => d.setAttribute('aria-current', j === i ? 'true' : 'false'));
      this.thumbs.forEach((t, j) => t.setAttribute('aria-current', j === i ? 'true' : 'false'));
    }
    goTo(i, instant) {
      i = Math.max(0, Math.min(i, this.slides.length - 1));
      this.index = i;
      const behavior = (instant || reducedMotion) ? 'auto' : 'smooth';
      if (this.stacked) { this.slides[i].scrollIntoView({ behavior, block: 'center' }); }
      else this.track.scrollTo({ left: this.step() * i, behavior });
      this.setActive(i);
    }
    goToMedia(mediaId, instant) {
      const idx = this.slides.findIndex(s => parseInt(s.dataset.mediaId, 10) === mediaId);
      if (idx > -1) this.goTo(idx, instant);
    }
  }

  /* -----------------------------------------------------------------------
     Quantity inputs
     ----------------------------------------------------------------------- */
  const Quantity = {
    init() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty-change]');
        if (!btn) return;
        const wrap = btn.closest('.qty');
        const input = $('input', wrap);
        const min = parseInt(input.min, 10) || 1;
        const max = parseInt(input.max, 10) || Infinity;
        let val = (parseInt(input.value, 10) || min) + parseInt(btn.dataset.qtyChange, 10);
        val = Math.max(min, Math.min(max, val));
        if (val !== parseInt(input.value, 10)) { input.value = val; input.dispatchEvent(new Event('change', { bubbles: true })); }
      });
    }
  };

  /* -----------------------------------------------------------------------
     Reveal on scroll
     ----------------------------------------------------------------------- */
  const Reveal = {
    init() {
      if (!document.body.classList.contains('has-reveal') || reducedMotion || !('IntersectionObserver' in window)) {
        $$('.reveal, .reveal-group').forEach(el => el.classList.add('is-visible')); return;
      }
      this.io = new IntersectionObserver((entries) => entries.forEach(en => {
        if (en.isIntersecting) { en.target.classList.add('is-visible'); this.io.unobserve(en.target); }
      }), { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
      this.observe(document);
    },
    observe(root) { $$('.reveal, .reveal-group', root).forEach(el => this.io ? this.io.observe(el) : el.classList.add('is-visible')); }
  };

  /* -----------------------------------------------------------------------
     Sticky add-to-cart (mobile)
     ----------------------------------------------------------------------- */
  const StickyATC = {
    init() {
      this.bar = $('[data-sticky-atc]');
      this.anchor = $('[data-sticky-anchor]');
      if (!this.bar || !this.anchor) return;
      const io = new IntersectionObserver(entries => {
        this.bar.classList.toggle('is-visible', !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0);
      }, { threshold: 0 });
      io.observe(this.anchor);
      const btn = $('[data-sticky-submit]', this.bar);
      if (btn) btn.addEventListener('click', () => { const f = $('form[data-product-form]'); if (f) f.requestSubmit(); });
    }
  };

  /* -----------------------------------------------------------------------
     Share (Web Share API → clipboard fallback)
     ----------------------------------------------------------------------- */
  const Share = {
    init() {
      document.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-share]');
        if (!btn) return;
        const data = { title: document.title, url: window.location.href };
        if (navigator.share) { try { await navigator.share(data); } catch (_) {} return; }
        try {
          await navigator.clipboard.writeText(data.url);
          const label = $('[data-share-label]', btn) || btn; const old = label.textContent;
          label.textContent = cfg.strings.shareCopied; setTimeout(() => label.textContent = old, 2000);
        } catch (_) {}
      });
    }
  };

  /* -----------------------------------------------------------------------
     Announcement rotation (multiple messages, gentle crossfade)
     ----------------------------------------------------------------------- */
  const Announcement = {
    init() {
      const bar = $('[data-announcement-rotate]');
      if (!bar) return;
      const items = $$('[data-announcement-item]', bar);
      if (items.length < 2 || reducedMotion) return;
      let i = 0;
      const delay = parseInt(bar.dataset.announcementRotate, 10) || 5000;
      setInterval(() => { items[i].hidden = true; i = (i + 1) % items.length; items[i].hidden = false; }, delay);
    }
  };

  /* -----------------------------------------------------------------------
     Facets: filter/sort forms re-render the product grid without reload
     ----------------------------------------------------------------------- */
  const Facets = {
    init() {
      this.forms = $$('form[data-facets-form]');
      if (!this.forms.length) return;
      this.forms.forEach(form => {
        form.addEventListener('change', debounce(() => this.apply(form), 300));
        form.addEventListener('submit', (e) => { e.preventDefault(); this.apply(form); });
      });
      document.addEventListener('click', (e) => {
        const link = e.target.closest('[data-facet-remove], [data-facet-clear], [data-pagination-link]');
        if (!link) return;
        e.preventDefault();
        this.load(link.href, !!link.closest('[data-pagination-link]'));
      });
      window.addEventListener('popstate', () => this.load(window.location.href, false, true));
    },
    apply(form) {
      const params = new URLSearchParams(new FormData(form));
      /* keep only meaningful params */
      const clean = new URLSearchParams();
      params.forEach((v, k) => { if (v !== '') clean.append(k, v); });
      const url = `${window.location.pathname}?${clean.toString()}`;
      this.load(url, false);
    },
    async load(url, scroll, skipHistory) {
      const grid = $('[data-collection-grid]');
      const sectionId = grid && grid.dataset.sectionId;
      if (!sectionId) { window.location.href = url; return; }
      grid.classList.add('is-loading');
      grid.setAttribute('aria-busy', 'true');
      try {
        const u = new URL(url, window.location.origin); u.searchParams.set('section_id', sectionId);
        const res = await fetch(u.toString());
        const html = new DOMParser().parseFromString(await res.text(), 'text/html');
        ['[data-collection-grid]', '[data-facets-sidebar]', '[data-facets-drawer-body]', '[data-facets-active]', '[data-collection-count]'].forEach(sel => {
          const t = $(sel), s = $(sel, html);
          if (t && s) t.innerHTML = s.innerHTML;
        });
        if (!skipHistory) history.pushState({}, '', url);
        if (scroll) grid.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
        Reveal.observe(grid);
      } catch (_) { window.location.href = url; }
      finally { grid.classList.remove('is-loading'); grid.removeAttribute('aria-busy'); }
    }
  };

  /* -----------------------------------------------------------------------
     Localization selectors
     ----------------------------------------------------------------------- */
  const Localization = {
    init() {
      $$('form[data-localization-form] select').forEach(sel => sel.addEventListener('change', (e) => {
        const form = e.target.closest('form');
        const hidden = $(`input[name="${e.target.dataset.name}"]`, form);
        if (hidden) { hidden.value = e.target.value; form.submit(); }
      }));
    }
  };

  /* -----------------------------------------------------------------------
     Product sections bootstrap (also re-run for Theme Editor)
     ----------------------------------------------------------------------- */
  function initProducts(root) {
    $$('[data-product-section]', root || document).forEach(section => {
      if (section.dataset.initialized) return;
      section.dataset.initialized = 'true';
      const galleryEl = $('[data-gallery]', section);
      if (galleryEl) section.gallery = new Gallery(galleryEl);
      new ProductForm(section);
    });
  }

  function init() {
    Header.init(); Drawers.init(); Cart.init(); Search.init(); Quantity.init();
    Reveal.init(); StickyATC.init(); Share.init(); Announcement.init(); Facets.init(); Localization.init();
    initProducts();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();

  /* Theme editor hooks */
  document.addEventListener('shopify:section:load', (e) => { initProducts(e.target); Reveal.observe(e.target); Header.el = $('[data-header]'); });
  document.addEventListener('shopify:section:select', (e) => {
    const drawer = $('[data-drawer]', e.target); if (drawer) Drawers.open(drawer.dataset.drawer);
  });
  document.addEventListener('shopify:section:deselect', () => Drawers.close());
})();
